import {
  CreateWorkspaceSchema,
  type ParsedCreateWorkspace,
  type ParsedWorkspace,
  WorkspaceSchema,
} from '@rhia/protocol';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '@/lib/api';

const WorkspacesArraySchema = z.array(WorkspaceSchema);

export function useWorkspaces() {
  return useQuery<ParsedWorkspace[], Error>({
    queryKey: ['workspaces'],
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/workspaces');
      return WorkspacesArraySchema.parse(raw);
    },
    staleTime: 60_000,
  });
}

export function useWorkspaceBySlug(slug: string) {
  return useQuery<ParsedWorkspace | null, Error>({
    queryKey: ['workspaces', 'by-slug', slug],
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/workspaces');
      const all = WorkspacesArraySchema.parse(raw);
      return all.find((w) => w.slug === slug) ?? null;
    },
    staleTime: 60_000,
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ParsedCreateWorkspace) => {
      CreateWorkspaceSchema.parse(input);
      const raw = await apiFetch<unknown>('/api/workspaces', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return WorkspaceSchema.parse(raw);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}
